import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../i18n";
import { Phone, Mail, MapPin, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { lang } = useLanguage();
  const t = useT(lang);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", area: "", city: "", class_type: t.class_types[0], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", area: "", city: "", class_type: t.class_types[0], message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-24 md:py-32">
      <div className="container-wc">
        <div className="max-w-3xl mb-16 fade-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">{t.nav.contact}</span>
          </div>
          <h1 className="h-display text-5xl md:text-6xl mb-6">{t.contact.title}</h1>
          <p className="text-dim text-lg leading-relaxed">{t.contact.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 card space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required data-testid="input-name" name="name" value={form.name} onChange={handleChange} placeholder={t.contact.name} className="field" />
              <input required data-testid="input-email" type="email" name="email" value={form.email} onChange={handleChange} placeholder={t.contact.email} className="field" />
              <input required data-testid="input-phone" name="phone" value={form.phone} onChange={handleChange} placeholder={t.contact.phone} className="field" />
              <input data-testid="input-city" name="city" value={form.city} onChange={handleChange} placeholder={t.contact.city} className="field" />
              <select data-testid="input-area" name="area" value={form.area} onChange={handleChange} className="field">
                <option value="">{t.contact.area}</option>
                {t.areas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select data-testid="input-class-type" name="class_type" value={form.class_type} onChange={handleChange} className="field">
                {t.class_types.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <textarea data-testid="input-message" name="message" value={form.message} onChange={handleChange} placeholder={t.contact.message} className="field" rows={5} />
            <button data-testid="submit-contact" type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
              {submitting ? "..." : t.contact.submit}
            </button>
          </form>

          {/* Info */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="card">
              <span className="font-cn text-gold text-3xl block mb-4">聯絡</span>
              <h3 className="font-serif text-2xl mb-6">{t.contact.info_title}</h3>
              <div className="space-y-4 text-sm">
                <a href="tel:+306979631031" data-testid="info-phone" className="flex items-center gap-3 text-dim hover:text-gold transition-colors">
                  <Phone size={16} className="text-gold" /> +30 6979 631031
                </a>
                <a href="mailto:info@wingchun.gr" data-testid="info-email" className="flex items-center gap-3 text-dim hover:text-gold transition-colors">
                  <Mail size={16} className="text-gold" /> info@wingchun.gr
                </a>
                <div className="flex items-center gap-3 text-dim">
                  <MapPin size={16} className="text-gold" /> {t.contact.location}
                </div>
              </div>
            </div>

            <div className="card bg-primary/10 border-primary/30">
              <h4 className="font-serif text-xl mb-2">{lang === "el" ? "Ώρες επικοινωνίας" : "Contact hours"}</h4>
              <p className="text-dim text-sm">{lang === "el" ? "Δευτέρα – Σάββατο" : "Monday – Saturday"}</p>
              <p className="text-gold text-lg font-serif mt-1">09:00 – 21:00</p>
            </div>
          </aside>
        </div>

        {/* FAQ */}
        <div className="mt-32">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">FAQ</span>
          </div>
          <h2 className="h-display text-4xl md:text-5xl mb-12">{t.contact.faq_title}</h2>
          <div className="space-y-2 max-w-3xl">
            {t.contact.faqs.map((f, i) => (
              <div key={f.q} className="border border-goldSoft" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-surface transition-colors"
                >
                  <span className="font-serif text-lg md:text-xl">{f.q}</span>
                  <ChevronDown size={20} className={`text-gold transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-dim leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
